// eslint-disable-next-line import/no-extraneous-dependencies
import { Drawer, Input } from 'antd'
import moment from 'moment'
import { GenericTable } from '../../components/GenericTable/GenericTable'
import { Loading } from '../../components/Loading/Loading'
import { Pagination } from '../../components/Pagination/Pagination'
import { DrillAlvo } from './useAnalyticsDrill'
import {
  DrillBarra,
  DrillBotaoExportar,
  DrillCabecalho,
  DrillConta,
  DrillContagem,
  DrillDefinicao,
  DrillIndisponivel,
  DrillLado,
  DrillLink,
  DrillOrdenar,
  DrillRecorte,
  DrillVazio,
  TableWrapper,
} from './PredictAnalytics.style'

type DrillDrawerProps = {
  alvo: DrillAlvo | null
  conjunto: ConjuntoAnalyticsType | null
  dados: GetAnalyticsEpisodiosType | null
  erro: string | null
  isLoading: boolean
  isExportando: boolean
  page: number
  setPage: (page: number) => void
  busca: string
  setBusca: (busca: string) => void
  ordenacao: { campo: string; direcao: 'asc' | 'desc' }
  fechar: () => void
  alternarLado: (conjunto: ConjuntoAnalyticsType) => void
  alternarOrdenacao: (campo: string) => void
  exportar: () => void
  /**
   * O usuário tem escopo por vendedor (PD-0004)? Vem por PROP, do painel, e não de um
   * `useTopUserData()` aqui dentro, por dois motivos: este componente tem early return no
   * topo (hook depois dele = "Rendered more hooks than during the previous render", e o
   * ESLint do projeto não tem o plugin `react-hooks` para avisar), e o hook não é Context
   * nem tem cache — dispararia 2 GETs a cada abertura do painel lateral.
   */
  temEscopoProprio: boolean
}

// Colunas da tela. `campo` presente = ordenável (tem de casar com a whitelist da API, que
// recusa qualquer outra propriedade com 400). "Situação na conta" e "Quem trabalhou" não
// ordenam: a primeira é derivada, a segunda não está na whitelist.
const COLUNAS: { titulo: string; campo?: string }[] = [
  { titulo: 'Cliente', campo: 'cliente' },
  { titulo: 'Produto', campo: 'produto' },
  { titulo: 'Data prevista', campo: 'dataProximaCompra' },
  { titulo: 'Status', campo: 'status' },
  { titulo: 'Situação na conta' },
  { titulo: 'Trabalhada em', campo: 'dataBaixa' },
  { titulo: 'Quem trabalhou' },
  { titulo: 'Vendedor atribuído', campo: 'vendedor' },
  { titulo: 'Venda creditada', campo: 'dataVendaCreditada' },
  { titulo: 'Episódio criado em', campo: 'dataCriacao' },
]

function formatarData(valor: string | null): string {
  if (!valor) {
    return '—'
  }
  return moment(valor, 'YYYY-MM-DD').format('DD/MM/YYYY')
}

function formatarDataHora(valor: string | null): string {
  if (!valor) {
    return '—'
  }
  return moment(valor, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm')
}

function formatarPct(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) {
    return 'Sem dado'
  }
  return `${(valor * 100).toFixed(1).replace('.', ',')}%`
}

function formatarNumero(valor: number | null): string {
  if (valor === null || valor === undefined) {
    return '—'
  }
  return valor.toLocaleString('pt-BR')
}

// A venda creditada tem duas confiabilidades diferentes: a DATA vem congelada no episódio e
// nunca falha; o detalhe é uma re-busca em venda_item, que é apagada e recriada a cada
// importação. Quando o detalhe some, a linha continua — dizer isso explicitamente evita que
// a ausência seja lida como perda de dado.
function VendaCreditada({ episodio }: { episodio: AnalyticsEpisodioType }) {
  if (!episodio.dataVendaCreditada) {
    return <>—</>
  }
  if (!episodio.vendaCreditada) {
    return (
      <>
        {formatarData(episodio.dataVendaCreditada)}
        <br />
        <DrillIndisponivel>
          detalhe indisponível (histórico reimportado)
        </DrillIndisponivel>
      </>
    )
  }
  const { quantidade, valorTotal, vendedorNome } = episodio.vendaCreditada
  return (
    <>
      {formatarData(episodio.dataVendaCreditada)}
      <br />
      <DrillIndisponivel>
        {formatarNumero(quantidade)} un
        {valorTotal
          ? ` · ${valorTotal.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}`
          : ''}
        {vendedorNome ? ` · ${vendedorNome}` : ''}
      </DrillIndisponivel>
    </>
  )
}

// Vazio nunca é uma tabela em branco: a mensagem diz POR QUE não há nada, distinguindo
// "nada aconteceu no período" de "o período antecede o histórico disponível".
function Vazio({ dados }: { dados: GetAnalyticsEpisodiosType }) {
  // `dados.busca` é o termo que a API de fato aplicou — não o texto ainda sendo digitado,
  // que durante o debounce nomearia uma busca que não aconteceu.
  if (dados.busca) {
    return (
      <DrillVazio>
        Nenhum episódio corresponde à busca <strong>{dados.busca}</strong>. O
        conjunto tem {formatarNumero(dados.totalConjunto)} episódio(s) — limpe a
        busca para ver todos.
      </DrillVazio>
    )
  }
  return (
    <DrillVazio>
      Nenhuma predição neste conjunto no período selecionado.
      <br />
      {dados.definicao}
      {dados.conjunto === 'AVALIADAS' && (
        <>
          <br />
          Como não há nenhuma predição avaliada, as taxas do painel aparecem
          como &quot;Sem dado&quot; — não como 0%.
        </>
      )}
      {dados.historicoDisponivelDesde && (
        <>
          <br />O histórico disponível começa em{' '}
          <strong>{formatarDataHora(dados.historicoDisponivelDesde)}</strong>.
          Períodos anteriores não têm episódios — seja por antecederem a
          ativação do Predict Analytics, seja pela retenção de 12 meses.
        </>
      )}
      {!dados.historicoDisponivelDesde && (
        <>
          <br />
          Esta organização ainda não tem histórico de predições registrado.
        </>
      )}
    </DrillVazio>
  )
}

// Rótulos legíveis do status bruto. Sem isto a coluna mostraria "NAO_REALIZADO" ao lado de
// uma "Situação na conta" que diz "Substituída" — e o usuário leria contradição onde a
// tabela justamente explica que aquela linha não conta como erro.
const STATUS_ROTULO: Record<string, string> = {
  PREDITO: 'Prevista',
  BAIXADO: 'Trabalhada',
  REALIZADO: 'Realizada',
  NAO_REALIZADO: 'Não realizada',
}

export function DrillDrawer({
  alvo,
  conjunto,
  dados,
  erro,
  isLoading,
  isExportando,
  page,
  setPage,
  busca,
  setBusca,
  ordenacao,
  fechar,
  alternarLado,
  alternarOrdenacao,
  exportar,
  temEscopoProprio,
}: DrillDrawerProps) {
  if (!alvo || !conjunto) {
    return null
  }

  const ladoAtual =
    alvo.denominador && conjunto === alvo.denominador.conjunto
      ? alvo.denominador
      : alvo.numerador

  // Sem `dados` o título cai no rótulo do quadro de origem — nunca em "Carregando…" fixo,
  // que ficaria preso para sempre se a requisição falhasse.
  const tituloCarregado = dados
    ? `${dados.rotulo} — ${formatarNumero(dados.totalConjunto)} episódio(s)`
    : alvo.quadro
  const titulo = !dados && !erro ? 'Carregando…' : tituloCarregado

  // O cabeçalho existe para a lista se explicar fora do contexto do card. Para
  // quem tem escopo por vendedor (PD-0004), "Todos" seria a única linha falsa
  // dele: o recorte veio do backend e não aparece em filtro nenhum da tela.
  const semFiltroDeVendedor = temEscopoProprio
    ? 'Todos os da sua carteira'
    : 'Todos'

  return (
    <Drawer
      open
      onClose={fechar}
      placement="right"
      width="min(1150px, 94vw)"
      title={titulo}
    >
      <DrillCabecalho>
        {/* A conta que originou o quadro. Alternar entre os dois lados sem fechar é o que
            transforma o painel em prova da divisão, e não em duas listas soltas. */}
        {alvo.denominador ? (
          <DrillConta>
            <span>{alvo.quadro}:</span>
            <DrillLado
              type="button"
              ativo={conjunto === alvo.numerador.conjunto}
              onClick={() => alternarLado(alvo.numerador.conjunto)}
            >
              {formatarNumero(alvo.numerador.valor)}
            </DrillLado>
            <span>÷</span>
            <DrillLado
              type="button"
              ativo={conjunto === alvo.denominador.conjunto}
              onClick={() =>
                alvo.denominador && alternarLado(alvo.denominador.conjunto)
              }
            >
              {formatarNumero(alvo.denominador.valor)}
            </DrillLado>
            <span>= {formatarPct(alvo.taxa)}</span>
          </DrillConta>
        ) : (
          <DrillConta>
            <span>
              {alvo.quadro}: <strong>{formatarNumero(ladoAtual.valor)}</strong>
            </span>
          </DrillConta>
        )}

        {dados && <DrillDefinicao>{dados.definicao}</DrillDefinicao>}

        <DrillRecorte>
          <span>
            <strong>Período:</strong>{' '}
            {dados ? formatarDataHora(dados.periodoInicio) : '—'} a{' '}
            {dados ? formatarDataHora(dados.periodoFim) : '—'}
          </span>
          <span>
            <strong>Vendedor:</strong>{' '}
            {dados && dados.vendedorId
              ? `id ${dados.vendedorId}`
              : semFiltroDeVendedor}
          </span>
          <span>
            O período recorta pela <strong>data de criação do episódio</strong>{' '}
            — não é a data da venda nem a data prevista.
          </span>
          <span>
            Esta lista é o mesmo conjunto que produziu o número do quadro.
          </span>
        </DrillRecorte>
      </DrillCabecalho>

      <DrillBarra>
        <DrillContagem>
          {dados && dados.busca
            ? `Exibindo ${formatarNumero(
                dados.episodios.numberOfElements
              )} de ${formatarNumero(
                dados.episodios.totalElements
              )} encontrados na busca · ${formatarNumero(
                dados.totalConjunto
              )} no conjunto`
            : `Exibindo ${formatarNumero(
                dados ? dados.episodios.numberOfElements : 0
              )} de ${formatarNumero(dados ? dados.totalConjunto : 0)}`}
        </DrillContagem>

        <Input
          allowClear
          style={{ width: 260 }}
          placeholder="Buscar cliente ou produto"
          value={busca}
          onChange={evento => setBusca(evento.target.value)}
        />

        {/* Exporta o conjunto INTEIRO, ignorando a busca: o arquivo é a prova do conjunto. */}
        <DrillBotaoExportar
          type="button"
          disabled={isExportando || !dados || dados.totalConjunto === 0}
          onClick={exportar}
        >
          {isExportando ? 'Gerando…' : 'Exportar conjunto completo'}
        </DrillBotaoExportar>
      </DrillBarra>

      {isLoading && <Loading contrast={false} />}

      {!isLoading && !dados && erro && (
        <DrillVazio>
          Não foi possível carregar os episódios: {erro}
          <br />
          Feche e abra o quadro novamente para tentar de novo.
        </DrillVazio>
      )}

      {!isLoading && dados && dados.episodios.content.length === 0 && (
        <Vazio dados={dados} />
      )}

      {!isLoading && dados && dados.episodios.content.length > 0 && (
        <>
          <TableWrapper>
            <GenericTable
              font="13px"
              spacing="12px"
              vendas
              headers={COLUNAS.map(coluna => {
                if (!coluna.campo) {
                  return coluna.titulo
                }
                return (
                  <DrillOrdenar
                    key={coluna.titulo}
                    type="button"
                    ativo={ordenacao.campo === coluna.campo}
                    onClick={() =>
                      coluna.campo && alternarOrdenacao(coluna.campo)
                    }
                  >
                    {coluna.titulo}
                    {ordenacao.campo === coluna.campo &&
                      (ordenacao.direcao === 'asc' ? ' ▲' : ' ▼')}
                  </DrillOrdenar>
                )
              })}
            >
              {dados.episodios.content.map(episodio => (
                <tr key={episodio.id}>
                  <td>
                    {episodio.clienteId ? (
                      <DrillLink
                        href={`/clientes/${episodio.clienteId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {episodio.clienteNome}
                      </DrillLink>
                    ) : (
                      episodio.clienteNome
                    )}
                  </td>
                  <td>{episodio.produtoNome}</td>
                  <td>{formatarData(episodio.dataProximaCompra)}</td>
                  <td>
                    {(episodio.status && STATUS_ROTULO[episodio.status]) ||
                      episodio.status}
                  </td>
                  <td>{episodio.situacaoNaContaRotulo}</td>
                  <td>{formatarDataHora(episodio.dataBaixa)}</td>
                  <td>{episodio.usuarioEmailBaixa || '—'}</td>
                  <td>{episodio.vendedorNome || 'Sem vendedor'}</td>
                  <td>
                    <VendaCreditada episodio={episodio} />
                  </td>
                  <td>{formatarDataHora(episodio.dataCriacao)}</td>
                </tr>
              ))}
            </GenericTable>
          </TableWrapper>

          {dados.episodios.totalElements > dados.episodios.size && (
            <Pagination
              pageNumber={page}
              pageSize={dados.episodios.totalElements}
              offSet={dados.episodios.size}
              onClick={setPage}
              label="episódios"
            />
          )}
        </>
      )}
    </Drawer>
  )
}
